import { apiGet } from './api-client';

const DROP = new Set([
  'id',
  'number',
  'internalNumber',
  'revision',
  'issues',
  'events',
  'crunchInvoiceId',
  'crunchClientId',
  'hubspotCompanyId',
  'sentAt',
  'sentTo',
  'invoicedNetPence',
  'invoicedVatPence',
  'invoicedPence',
  'remainingPence',
]);

export function editableStatus(status: unknown, issuedFallback: string): string {
  const value = String(status || '');
  if (value === 'issued' || value === 'paid' || value === 'credited') return issuedFallback;
  return value || issuedFallback;
}

export function copyDocument(row: Record<string, unknown>, status: string): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (DROP.has(key)) continue;
    next[key] = value;
  }
  if (Array.isArray(next.lines)) {
    next.lines = (next.lines as Record<string, unknown>[]).map((line) => {
      const copy = { ...line };
      delete copy.id;
      return copy;
    });
  }
  next.status = status;
  return next;
}

function storageKey(kind: string): string {
  return `bo-duplicate:${kind}`;
}

export function stashDuplicate(kind: string, row: Record<string, unknown>): void {
  sessionStorage.setItem(storageKey(kind), JSON.stringify(row));
}

export function takeDuplicate(kind: string): Record<string, unknown> | null {
  const raw = sessionStorage.getItem(storageKey(kind));
  if (!raw) return null;
  sessionStorage.removeItem(storageKey(kind));
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
}

export function peekDuplicate(kind: string): boolean {
  return sessionStorage.getItem(storageKey(kind)) != null;
}

export function blankVisibleKey(url: string, draft: Record<string, unknown>): Record<string, unknown> {
  const key = {
    '/api/customers': 'code',
    '/api/engagements': 'name',
    '/api/charge-lines': 'name',
    '/api/vendors': 'name',
    '/api/purchase-orders': 'poNumber',
    '/api/purchase-orders?direction=supplier': 'poNumber',
  }[url];
  if (!key) return draft;
  return { ...draft, [key]: '' };
}

export function offerDraft(row: Record<string, unknown>): Record<string, unknown> {
  const copy = copyDocument(row, 'draft');
  delete copy.proposalId;
  delete copy.sharePointUrl;
  delete copy.sharePointPath;
  delete copy.pdfHash;
  copy.title = '';
  return copy;
}

export async function loadOfferCopies(proposalId: string | undefined, quotationId: string | undefined, which: 'proposal' | 'quotation' | 'both'): Promise<{ proposal: Record<string, unknown> | null; quotation: Record<string, unknown> | null }> {
  const [proposal, quotation] = await Promise.all([
    which !== 'quotation' && proposalId ? apiGet<Record<string, unknown>>(`/api/proposals/${proposalId}`) : Promise.resolve(null),
    which !== 'proposal' && quotationId ? apiGet<Record<string, unknown>>(`/api/quotations/${quotationId}`) : Promise.resolve(null),
  ]);
  if (!proposal && !quotation) throw new Error('Offer not found');
  return {
    proposal: proposal ? offerDraft(proposal) : null,
    quotation: quotation ? offerDraft(quotation) : null,
  };
}
